import React, { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom';

// STYLES
import globalStyles from "../../../Styles/global.module.scss"
import navbarStyles from "../../../Styles/ComponentStyles/MainPages/AppNavbarChildren.module.scss"
import styles from "../../../Styles/ComponentStyles/MainPages/dashboard.module.scss"
import loader from "../../../Assets/Dashboard/pdfloader.gif"

// MUI
import Grid from '@mui/material/Grid2';
import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import deleteIcon from "../../../Assets/Member/Delete-Icons.svg"
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';

// GLOBAL 
import AppNavbar from '../../../Components/AppNavbar/AppNavbar';
import DashboardNavbar from '../DashboardNavbar/DashboardNavbar';
import GlobalTextfield from '../../../Utils/Layout/GlobalTextfield/GlobalTextfield';
import GlobalDialog from '../../../Utils/Layout/GlobalDialog/GlobalDialog';
import GlobalWarningDialog from '../../../Utils/Layout/GlobalWarningDialog/GlobalWarningDialog';
import GlobalButton from '../../../Utils/Layout/GlobalButton/GlobalButton';
import { useSnackbar } from '../../../Utils/Layout/GlobalSnackbar/GlobalSnackbar';
import { useIsBelow1200, useIsBelow600 } from '../../../Utils/Constant/Constant';

// COMPONENTS    
import FeeImpactChart from './FeeImpactChart';
import ComplianceDocument from '../../../Components/ComplianceDocument/ComplianceDocument';
import Plan401kChildren from '../DashboardNavbar/DashboardNavbarChildren/Plan401kChildren';

// API CONTEXT
import { DashboardContext, DashboardDetailPayload } from '../../../Context/DashboardDetailContext';
import { GlobalUserContext, GlobalUserContextPayload } from '../../../Context/GlobalUserDetailContext';
import agent from '../../../Services/api';
import { Plan401kPayloadModel } from '../../../models/axiosModels';
import GlobalPDFLoader from '../../../Utils/Layout/GlobalPDFLoader/GlobalPDFLoader';
import { plan401kDetailsInitialize } from '../../../Utils/Constant/DashboardConstant';

const DashboardPlan401k = () => {

    const {
        plan401kDetails,
        setPlan401kDetails
    } = useContext(DashboardContext) as DashboardDetailPayload

    const {
        setFlagLoader,
        userProfileDetails
    } = useContext(GlobalUserContext) as GlobalUserContextPayload

    const isProfileAdmin = userProfileDetails?.user?.flagAdmin
    const resultRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate()
    const isBelow600 = useIsBelow600();
    const isBelow1200 = useIsBelow1200();
    const { showSnackbar } = useSnackbar();

    const [isCalculated, setIsCalculated] = useState(false);
    const [flagOpenUpdateCalculateDialog, setFlagOpenUpdateCalculateDialog] = useState(false);
    const [showAllYears, setShowAllYears] = useState(false);
    const [tableFocusableFields, setTableFocusableFields] = useState({
        numberOfEmployees: false,
        setUpFee: false,
        conversionFee: false,
        adminFee: false,
        perParticipationAnnualFee: false,
        otherFee: false,
        // ASSETS   
        advisorFee: false,
        otherAssetsFees: false,
        avgFundExpense: false,
        recordAssetFee: false,
    });
    const [editableFeeIndexes, setEditableFeeIndexes] = useState<number[]>([]);
    const [PDFLoader, setPDFLoader] = useState(false);
    const [feeNameErrors, setFeeNameErrors] = useState<{ [key: number]: string }>({});

    const displayedYearData = showAllYears ? plan401kDetails?.feesOnBalance?.year : plan401kDetails?.feesOnBalance?.year?.filter(y => [1, 5, 10, 15, 20].includes(y.year));
    const lastCheckedAcrylicRef = useRef<any>(null);
    const hasInitializedRef = useRef(false);

    const acrylicFees = plan401kDetails?.fees?.acrylic?.annualFeesPaidByBusiness?.additionalOtherFees || [];
    const otherCompanyFees = plan401kDetails?.fees?.otherCompany?.annualFeesPaidByBusiness?.additionalOtherFees || [];

    const filteredAcrylicFees = acrylicFees?.filter(f => f.feeName || f.feeValue);
    const filteredOtherFees = otherCompanyFees?.filter(f => f.feeName || f.feeValue);
    const maxLength = Math?.min(2, Math.max(filteredAcrylicFees.length, filteredOtherFees.length));

    useEffect(() => {
        fetchAcrylicFeeDetails()
    }, [])

    useEffect(() => {
        const acrylic = plan401kDetails?.fees?.acrylic;

        // Define what counts as "real" acrylic data (not just default blank)
        const isRealAcrylicData = !!acrylic?.annualFeesPaidByBusiness?.totalAnnualFeesPaidByBusiness;

        if (!hasInitializedRef.current && isRealAcrylicData) {
            lastCheckedAcrylicRef.current = JSON.parse(JSON.stringify(acrylic));
            hasInitializedRef.current = true;
        }
    }, [plan401kDetails?.fees?.acrylic?.annualFeesPaidByBusiness?.totalAnnualFeesPaidByBusiness]);

    // HANDLE 
    const getDisplayFeeName = (feeName: string, index: number) => {
        if (feeName && feeName.trim() !== "") {
            return feeName;
        }

        // Show nothing for new row until user edits
        return editableFeeIndexes.includes(index) ? `Other Fee ${index + 2}` : "";
    };

    const handleClick = () => {
        navigate('/')
        setPlan401kDetails(plan401kDetailsInitialize);
    }

    function formatWithCommas(raw: string): string {
        // Split integer and fraction
        const [intPart, fracPart] = raw.split('.');
        const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return fracPart !== undefined ? `${withCommas}.${fracPart}` : withCommas;
    }

    function formatDollar(raw: string): string {
        if (raw === '' || raw === '.') {
            // If the user has just typed "." or cleared the field, show empty
            return raw;
        }
        return '$' + formatWithCommas(raw);
    }

    function parseCurrency(val: string): number {
        return parseFloat(val.replace(/[$,]/g, '')) || 0;
    }

    function percentToNumber(val: string): number {
        return parseFloat(val.replace(/%/g, '')) || 0;
    }

    const getFieldOrder = (company: any) => {
        return [
            'acrylicAndAdvisorFee',
            'otherAssetsBasedFee',
            'averageMutualFundExpense',
            'recordKeeperAssetFee',
        ];
    };

    const getBusinessFeeFieldOrder = (company: 'acrylic' | 'otherCompany') => {
        const staticFields = [
            'numberOfEmployees',
            'setUpFee',
            'conversionFee',
            'adminFee',
            'perParticipationAnnualFee',
            'otherFee'
        ];

        const dynamicFees = plan401kDetails?.fees?.[company]?.annualFeesPaidByBusiness?.additionalOtherFees || [];
        const dynamicFields = dynamicFees.map((_, index) => `additionalOtherFees-${index}`);
        return [...staticFields, ...dynamicFields];
    };

    const getFieldOrderBySection = (section: 'planAssets' | 'businessFees', company: 'acrylic' | 'otherCompany') => {
        if (section === 'planAssets') return getFieldOrder(company);
        if (section === 'businessFees') return getBusinessFeeFieldOrder(company);
        return [];
    };

    const focusField = (company: any, field: any) => {
        const fieldId = `${company}-${field}`;
        const element = document.querySelector(`[data-field-id="${fieldId}"]`);
        if (element && 'focus' in element) {
            (element as HTMLInputElement).focus();
        }
    };

    const isSaveDisabled = (index: number) => {
        return !!feeNameErrors[index];
    };

    const isDeepEqual = (obj1: any, obj2: any): boolean => {
        return JSON.stringify(obj1) === JSON.stringify(obj2);
    };

    // API
    const fetchAcrylicFeeDetails = async () => {

        setFlagLoader(true)
        const response = await agent.getAcrylicFeeDetailsApi.get();
        console.log("log of response of getAcrylicFeeDetailsApi", response);

        if (response?.status) {
            const data = response.data;

            const toCurrency = (value: string) => value !== undefined && value !== null && value !== "" ? `$${parseFloat(value).toLocaleString()}` : "";
            const toPercentage = (value: string) => value !== undefined && value !== null && value !== "" ? `${parseFloat(value)}%` : "";

            const parseCurrency = (val: string) => parseFloat(val || "0") || 0;
            const percentToNumber = (val: string) => parseFloat(val || "0") || 0;

            const business = data.fee.acrylic.annualFeesPaidByBusiness;
            const assets = data.fee.acrylic.annualFeesPaidByPlanAssets;

            // ----- CALCULATE BUSINESS TOTAL -----
            const flatSum = parseCurrency(business.setUpFee) + parseCurrency(business.conversionFee) + parseCurrency(business.adminFee);
            const perParticipant = parseCurrency(business.numberOfEmployees) * parseCurrency(business.perParticipationAnnualFee);

            const otherFee = parseCurrency(business.otherFee);
            const additionalSum = (business.additionalOtherFees || [])?.reduce((sum: any, f: any) => sum + parseCurrency(f.feeValue), 0);
            const totalBusiness = flatSum + perParticipant + otherFee + additionalSum;

            // ----- CALCULATE PLAN ASSET FEE TOTAL -----
            const totalAssets = percentToNumber(assets.acrylicAndAdvisorFee) + percentToNumber(assets.otherAssetsBasedFee) + percentToNumber(assets.averageMutualFundExpense) + percentToNumber(assets.recordKeeperAssetFee);

            setPlan401kDetails(prev => ({
                ...prev,
                planAssumptions: {
                    companyName: data.planAssumptions.companyName.toLowerCase().trim() === "acrylic" ? "OTHER COMPANY" : data.planAssumptions.companyName,
                    rollover: toCurrency(data.planAssumptions.rollover || ""),
                    annualContribution: toCurrency(data.planAssumptions.annualContribution || ""),
                    annualPlanGrowth: toPercentage(data.planAssumptions.annualPlanGrowth || "")
                },
                fees: {
                    ...prev.fees,
                    acrylic: {
                        annualFeesPaidByBusiness: {
                            numberOfEmployees: business.numberOfEmployees || "",
                            setUpFee: toCurrency(business.setUpFee),
                            conversionFee: toCurrency(business.conversionFee || ""),
                            adminFee: toCurrency(business.adminFee || ""),
                            perParticipationAnnualFee: toCurrency(business.perParticipationAnnualFee || ""),
                            otherFee: toCurrency(business.otherFee || ""),
                            additionalOtherFees: business.additionalOtherFees || [{ feeName: "", feeValue: "" }],
                            totalAnnualFeesPaidByBusiness: toCurrency(totalBusiness.toFixed(2))
                        },
                        annualFeesPaidByPlanAssets: {
                            acrylicAndAdvisorFee: toPercentage(assets.acrylicAndAdvisorFee || ""),
                            otherAssetsBasedFee: toPercentage(assets.otherAssetsBasedFee || ""),
                            averageMutualFundExpense: toPercentage(assets.averageMutualFundExpense || ""),
                            recordKeeperAssetFee: toPercentage(assets.recordKeeperAssetFee || ""),
                            totalAannualFeesPaidByPlanAssets: `${totalAssets.toFixed(2)}%`
                        }
                    },
                    otherCompany: plan401kDetailsInitialize?.fees?.otherCompany
                }
            }));
            setFlagLoader(false)
        }
        else {
            console.log("Failed to fetch acrylic fee details.");
            const errorMessage = response?.data?.error?.message || 'Something Went Wrong!';
            showSnackbar(errorMessage, 'error');
            setFlagLoader(false)
        }
    };

    const handleSaveAcrylicSaveDetails = async () => {
        const acrylic = plan401kDetails?.fees?.acrylic;
        const payload: Plan401kPayloadModel = {
            acrylic: {
                annualFeesPaidByBusiness: {
                    numberOfEmployees: acrylic?.annualFeesPaidByBusiness.numberOfEmployees,
                    setUpFee: parseCurrency(acrylic?.annualFeesPaidByBusiness.setUpFee)?.toString(),
                    conversionFee: parseCurrency(acrylic?.annualFeesPaidByBusiness.conversionFee)?.toString(),
                    adminFee: parseCurrency(acrylic?.annualFeesPaidByBusiness.adminFee)?.toString(),
                    perParticipationAnnualFee: parseCurrency(acrylic?.annualFeesPaidByBusiness.perParticipationAnnualFee)?.toString(),
                    otherFee: parseCurrency(acrylic?.annualFeesPaidByBusiness?.otherFee)?.toString(),
                    additionalOtherFees: acrylic?.annualFeesPaidByBusiness?.additionalOtherFees?.map(item => ({
                        feeName: item?.feeName,
                        feeValue: parseCurrency(item?.feeValue).toString()
                    })),
                },
                annualFeesPaidByPlanAssets: {
                    acrylicAndAdvisorFee: percentToNumber(acrylic?.annualFeesPaidByPlanAssets?.acrylicAndAdvisorFee)?.toString(),
                    otherAssetsBasedFee: percentToNumber(acrylic?.annualFeesPaidByPlanAssets?.otherAssetsBasedFee)?.toString(),
                    averageMutualFundExpense: percentToNumber(acrylic?.annualFeesPaidByPlanAssets?.averageMutualFundExpense)?.toString(),
                    recordKeeperAssetFee: percentToNumber(acrylic?.annualFeesPaidByPlanAssets?.recordKeeperAssetFee)?.toString()
                }
            }
        };
        setFlagLoader(true)
        const response = await agent?.saveAcrylicFeeDetails.post(payload)
        if (response?.status) {
            console.log("response of saveAcrylicFeeDetails", response);
            const message = response?.message || 'Fee data saved successfully!';
            showSnackbar(message, 'success');
            // await fetchAcrylicFeeDetails()
            setFlagLoader(false)
        }
        else {
            console.log("response false of saveAcrylicFeeDetails", response);
            const errorMessage = response?.data?.error?.message || 'Something Went Wrong!';
            showSnackbar(errorMessage, 'error');
            setFlagLoader(false)
        }
    }

    const handleFeeComparisonPdf = async (data?: any) => {
        const feesOnBalance = await handleCalculateFeesImpact(true);
        const stripCurrency = (val: string) => val?.replace(/[$,%]/g, '').trim();

        const sanitizeFees = (fees: any, company: "acrylic" | "otherCompany") => {
            const otherFeeValue = stripCurrency(fees.annualFeesPaidByBusiness.otherFee);

            const filteredAdditionalFees = (fees.annualFeesPaidByBusiness.additionalOtherFees || [])
                .filter((fee: any) => {
                    const isEmpty = !(fee.feeName || fee.feeValue);
                    const isDuplicateOtherFee =
                        company === "otherCompany" &&
                        stripCurrency(fee.feeValue) === otherFeeValue &&
                        (fee.feeName === "" || fee.feeName?.toLowerCase().startsWith("other fee"));

                    return !isEmpty && !isDuplicateOtherFee;
                })
                .slice(0, 2)
                .map((fee: any) => ({
                    feeName: fee.feeName,
                    feeValue: stripCurrency(fee.feeValue)
                }));


            return {
                annualFeesPaidByBusiness: {
                    numberOfEmployees: fees.annualFeesPaidByBusiness.numberOfEmployees || "",
                    setUpFee: stripCurrency(fees.annualFeesPaidByBusiness.setUpFee),
                    conversionFee: stripCurrency(fees.annualFeesPaidByBusiness.conversionFee),
                    adminFee: stripCurrency(fees.annualFeesPaidByBusiness.adminFee),
                    perParticipationAnnualFee: stripCurrency(fees.annualFeesPaidByBusiness.perParticipationAnnualFee),
                    otherFee: otherFeeValue,
                    additionalOtherFees: filteredAdditionalFees,
                    totalAnnualFeesPaidByBusiness: stripCurrency(fees.annualFeesPaidByBusiness.totalAnnualFeesPaidByBusiness)
                },
                annualFeesPaidByPlanAssets: {
                    acrylicAndAdvisorFee: stripCurrency(fees.annualFeesPaidByPlanAssets.acrylicAndAdvisorFee),
                    otherAssetsBasedFee: stripCurrency(fees.annualFeesPaidByPlanAssets.otherAssetsBasedFee),
                    averageMutualFundExpense: stripCurrency(fees.annualFeesPaidByPlanAssets.averageMutualFundExpense),
                    recordKeeperAssetFee: stripCurrency(fees.annualFeesPaidByPlanAssets.recordKeeperAssetFee),
                    totalAnnualFeesPaidByPlanAssets: stripCurrency(fees.annualFeesPaidByPlanAssets.totalAannualFeesPaidByPlanAssets)
                }
            };
        };

        const payload = {
            planAssumptions: {
                companyName: plan401kDetails.planAssumptions.companyName,
                rollover: stripCurrency(plan401kDetails.planAssumptions.rollover),
                annualContribution: stripCurrency(plan401kDetails.planAssumptions.annualContribution),
                annualPlanGrowth: stripCurrency(plan401kDetails.planAssumptions.annualPlanGrowth)
            },
            fees: {
                acrylic: sanitizeFees(plan401kDetails.fees.acrylic, "acrylic"),
                otherCompany: sanitizeFees(plan401kDetails.fees.otherCompany, "otherCompany")
            },
            feesOnBalance: {
                year: (data)?.map((y: any) => ({
                    year: y.year,
                    totalDifference: y.totalDifference,
                    yearEndingBalance: {
                        acrylic: y.yearEndingBalance.acrylic,
                        otherCompany: y.yearEndingBalance.otherCompany
                    },
                    saving: {
                        dueToDirectFees: y.saving.dueToDirectFees,
                        dueToPlanAssetsFees: y.saving.dueToPlanAssetsFees
                    }
                }))
            }
        };

        setPDFLoader(true)
        const response = await agent.feeComparisonPDFApi.post(payload)
        console.log("response of feeComparisonPDFApi", response);
        if (response?.status) {
            const base64 = response.data.pdfBase64;
            const byteCharacters = atob(base64);
            const byteNumbers = Array.from(byteCharacters, char => char.charCodeAt(0));
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = '401k_fee_comparison.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            setPDFLoader(false)
            showSnackbar("PDF Download Successfully!", 'success');
        }
        else {
            const errorMessage = response?.data?.error?.message || 'Something Went Wrong!';
            showSnackbar(errorMessage, 'error');
            setPDFLoader(false)
        }
    }


    // HANDLE CHANGE
    const handleAssumptionsChange = (
        key: keyof typeof plan401kDetails.planAssumptions,
        rawValue: string,
        isBlur?: boolean
    ) => {
        let value = rawValue;

        if (key === "companyName") {
            if (value.length > 30) return;
        }

        if (key === "rollover" || key === "annualContribution") {
            // Remove all non-digit characters first, then format with commas and $
            const cleaned = value.replace(/[^0-9]/g, "");
            const formatted = cleaned ? `$${parseInt(cleaned).toLocaleString("en-US")}` : "";
            value = formatted;
        }

        if (key === "annualPlanGrowth") {
            let cleaned = value.replace(/[^0-9.]/g, '');

            const parts = cleaned.split('.');
            if (parts.length > 2) return;

            const numeric = parseFloat(cleaned);
            if (isNaN(numeric)) {
                value = "";
            }
            else if (numeric > 100) {
                return;
            }
            else {
                value = isBlur && cleaned ? `${numeric.toString()}%` : cleaned;
            }
        }


        setPlan401kDetails(prev => ({
            ...prev,
            planAssumptions: {
                ...prev.planAssumptions,
                [key]: value
            }
        }));

        setIsCalculated(false);
    };

    const handleBusinessFeeChange = (
        company: "acrylic" | "otherCompany",
        field: keyof typeof plan401kDetails.fees.acrylic.annualFeesPaidByBusiness | "additionalOtherFees",
        rawValue: string,
        index?: any,
        subField?: "feeName" | "feeValue"
    ) => {
        setPlan401kDetails((prev: any) => {
            const toNum = (v: string) => parseCurrency(v);

            // Handle feeName changes - sync across both companies
            if (field === "additionalOtherFees" && typeof index === "number" && subField === "feeName") {
                const trimmedValue = rawValue.trim();

                // Validation: Check for empty or duplicate fee names
                const currentCompanyData = prev.fees[company].annualFeesPaidByBusiness;
                const isDuplicate = currentCompanyData.additionalOtherFees.some((fee: any, i: number) =>
                    i !== index && fee.feeName.trim().toLowerCase() === trimmedValue.toLowerCase()
                );

                // Set validation error
                setFeeNameErrors(prevErrors => {
                    const newErrors = { ...prevErrors };
                    if (!trimmedValue) {
                        newErrors[index] = "Fee name must be unique and non-empty";
                    } else if (isDuplicate) {
                        newErrors[index] = "Fee name must be unique and non-empty";
                    } else {
                        delete newErrors[index];
                    }
                    return newErrors;
                });

                // Don't update state if validation fails, but still allow the input to show
                // This allows user to type and see the error

                // Update fee name for both companies
                const acrylicFees = [...prev.fees.acrylic.annualFeesPaidByBusiness.additionalOtherFees];
                const otherCompanyFees = [...prev.fees.otherCompany.annualFeesPaidByBusiness.additionalOtherFees];

                // Ensure both arrays have the same length and update the fee name
                if (acrylicFees[index]) {
                    acrylicFees[index] = { ...acrylicFees[index], feeName: rawValue };
                }
                if (otherCompanyFees[index]) {
                    otherCompanyFees[index] = { ...otherCompanyFees[index], feeName: rawValue };
                }

                // Calculate totals for both companies
                const calculateTotal = (companyData: any, additionalFees: any[]) => {
                    const flatSum = toNum(companyData.setUpFee) + toNum(companyData.conversionFee) + toNum(companyData.adminFee);
                    const perParticipant = toNum(companyData.numberOfEmployees) * toNum(companyData.perParticipationAnnualFee);
                    const otherFeeVal = toNum(companyData.otherFee);
                    const additionalSum = additionalFees.reduce((acc, fee) => acc + toNum(fee.feeValue), 0);
                    return flatSum + perParticipant + otherFeeVal + additionalSum;
                };

                const acrylicTotal = calculateTotal(prev.fees.acrylic.annualFeesPaidByBusiness, acrylicFees);
                const otherCompanyTotal = calculateTotal(prev.fees.otherCompany.annualFeesPaidByBusiness, otherCompanyFees);

                return {
                    ...prev,
                    fees: {
                        ...prev.fees,
                        acrylic: {
                            ...prev.fees.acrylic,
                            annualFeesPaidByBusiness: {
                                ...prev.fees.acrylic.annualFeesPaidByBusiness,
                                additionalOtherFees: acrylicFees,
                                totalAnnualFeesPaidByBusiness: formatDollar(acrylicTotal.toFixed(2))
                            }
                        },
                        otherCompany: {
                            ...prev.fees.otherCompany,
                            annualFeesPaidByBusiness: {
                                ...prev.fees.otherCompany.annualFeesPaidByBusiness,
                                additionalOtherFees: otherCompanyFees,
                                totalAnnualFeesPaidByBusiness: formatDollar(otherCompanyTotal.toFixed(2))
                            }
                        }
                    }
                };
            }

            // Handle all other changes (regular fields and feeValue)
            const companyData = prev.fees[company].annualFeesPaidByBusiness;
            let updatedAdditionalFees = [...companyData.additionalOtherFees];

            if (field === "additionalOtherFees" && typeof index === "number" && subField === "feeValue") {
                const stripped = rawValue.replace(/[$,]/g, '');
                if (!/^\d*\.?\d*$/.test(stripped)) {
                    return prev;
                }
                updatedAdditionalFees[index] = {
                    ...updatedAdditionalFees[index],
                    feeValue: formatDollar(stripped)
                };
            }

            // Update the company data
            const updatedCompanyData = {
                ...companyData,
                // Update the specific field if it's not additionalOtherFees
                ...(field !== "additionalOtherFees" ? {
                    [field]: field === "numberOfEmployees" ? rawValue.replace(/\D/g, '') : formatDollar(rawValue.replace(/[$,]/g, ''))
                } : {}),
                additionalOtherFees: updatedAdditionalFees
            };

            // Calculate the total
            const flatSum = toNum(updatedCompanyData.setUpFee) + toNum(updatedCompanyData.conversionFee) + toNum(updatedCompanyData.adminFee);
            const perParticipant = toNum(updatedCompanyData.numberOfEmployees) * toNum(updatedCompanyData.perParticipationAnnualFee);
            const otherFeeVal = toNum(updatedCompanyData.otherFee);
            const additionalSum = updatedAdditionalFees.reduce((acc, fee) => acc + toNum(fee.feeValue), 0);
            const newTotal = flatSum + perParticipant + otherFeeVal + additionalSum;

            // Return the updated state
            return {
                ...prev,
                fees: {
                    ...prev.fees,
                    [company]: {
                        ...prev.fees[company],
                        annualFeesPaidByBusiness: { ...updatedCompanyData, totalAnnualFeesPaidByBusiness: formatDollar(newTotal.toFixed(2)) }
                    }
                }
            };
        });

        setIsCalculated(false);
    };

    const handlePlanAssetsChange = (
        company: "acrylic" | "otherCompany",
        field: keyof typeof plan401kDetails.fees.acrylic.annualFeesPaidByPlanAssets,
        rawValue: string,
        isBlur?: boolean
    ) => {
        let value = rawValue.replace(/[^\d.]/g, ''); // strip all non-digit/dot chars

        // Prevent invalid formats like multiple dots
        if (!/^\d*\.?\d*$/.test(value)) return;

        if (value !== '' && parseFloat(value) > 100) return;

        // Add % only on blur
        const finalValue = isBlur && value ? `${parseFloat(value).toString()}%` : value;

        setPlan401kDetails(prev => {
            const current = prev.fees[company].annualFeesPaidByPlanAssets;
            const updated: typeof current = { ...current, [field]: finalValue };

            const toNum = (v: string) => percentToNumber(v);
            const total = toNum(updated.acrylicAndAdvisorFee) + toNum(updated.otherAssetsBasedFee) + toNum(updated.averageMutualFundExpense) + toNum(updated.recordKeeperAssetFee);
            updated.totalAannualFeesPaidByPlanAssets = `${total.toFixed(2)}%`;

            return {
                ...prev,
                fees: {
                    ...prev.fees,
                    [company]: {
                        ...prev.fees[company],
                        annualFeesPaidByPlanAssets: updated
                    }
                }
            };
        });

        setIsCalculated(false);
    };

    const handleCustomNavigation = (
        e: any,
        section: 'planAssets' | 'businessFees',
        currentCompany: 'acrylic' | 'otherCompany',
        currentField: string
    ) => {
        const fieldOrder = getFieldOrderBySection(section, currentCompany);
        const currentIndex = fieldOrder.indexOf(currentField);

        if (e.key === 'Tab') {
            e.preventDefault();
            const nextIndex = e.shiftKey ? (currentIndex - 1 + fieldOrder.length) % fieldOrder.length : (currentIndex + 1) % fieldOrder.length;
            focusField(currentCompany, fieldOrder[nextIndex]);
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prevIndex = currentIndex > 0 ? currentIndex - 1 : fieldOrder.length - 1;
            focusField(currentCompany, fieldOrder[prevIndex]);
        }
        else if (e.key === 'ArrowDown') {
            e.preventDefault();
            const nextIndex = currentIndex < fieldOrder.length - 1 ? currentIndex + 1 : 0;
            focusField(currentCompany, fieldOrder[nextIndex]);
        }
    };

    const handleAddAdditionalOtherFee = () => {
        setPlan401kDetails(prev => {
            const acrylicFees = [...prev.fees.acrylic.annualFeesPaidByBusiness.additionalOtherFees];
            const otherCompanyFees = [...prev.fees.otherCompany.annualFeesPaidByBusiness.additionalOtherFees];

            if (acrylicFees.length >= 2) return prev; // allow only 2 additional rows

            // Generate unique fee name
            const existingFeeNames = acrylicFees.map(fee => fee.feeName.toLowerCase());
            let feeNumber = 2;
            let nextFeeName = `Other Fee ${feeNumber}`;

            while (existingFeeNames.includes(nextFeeName.toLowerCase()) && feeNumber <= 10) {
                feeNumber++;
                nextFeeName = `Other Fee ${feeNumber}`;
            }

            const newFee = { feeName: nextFeeName, feeValue: "" };

            // const nextFeeName = `Other Fee ${acrylicFees.length + 2}`; // will be 2 or 3
            // const newFee = { feeName: nextFeeName, feeValue: "" };

            return {
                ...prev,
                fees: {
                    acrylic: {
                        ...prev.fees.acrylic,
                        annualFeesPaidByBusiness: {
                            ...prev.fees.acrylic.annualFeesPaidByBusiness,
                            additionalOtherFees: [...acrylicFees, newFee]
                        }
                    },
                    otherCompany: {
                        ...prev.fees.otherCompany,
                        annualFeesPaidByBusiness: {
                            ...prev.fees.otherCompany.annualFeesPaidByBusiness,
                            additionalOtherFees: [...otherCompanyFees, newFee]
                        }
                    }
                }
            };
        });
        setIsCalculated(false);

    };

    const handleRemoveAdditionalOtherFee = (index: number) => {
        setPlan401kDetails(prev => {
            const recalculateTotal = (companyKey: "acrylic" | "otherCompany") => {
                const current = prev.fees[companyKey].annualFeesPaidByBusiness;
                const newAdditionalFees = current.additionalOtherFees.filter((_, i) => i !== index);

                const flatSum = parseCurrency(current.setUpFee) + parseCurrency(current.conversionFee) + parseCurrency(current.adminFee);
                const perParticipant = parseCurrency(current.numberOfEmployees) * parseCurrency(current.perParticipationAnnualFee);
                const otherFee = parseCurrency(current.otherFee);
                const additionalSum = newAdditionalFees.reduce((sum, fee) => sum + parseCurrency(fee.feeValue), 0);

                const total = flatSum + perParticipant + otherFee + additionalSum;

                return {
                    ...current,
                    additionalOtherFees: newAdditionalFees,
                    totalAnnualFeesPaidByBusiness: formatDollar(total.toFixed(2))
                };
            };

            return {
                ...prev,
                fees: {
                    acrylic: {
                        ...prev.fees.acrylic,
                        annualFeesPaidByBusiness: recalculateTotal("acrylic")
                    },
                    otherCompany: {
                        ...prev.fees.otherCompany,
                        annualFeesPaidByBusiness: recalculateTotal("otherCompany")
                    }
                }
            };
        });

        // Clear validation errors for removed index and adjust remaining indexes
        setFeeNameErrors(prevErrors => {
            const newErrors: { [key: number]: string } = {};
            Object.keys(prevErrors).forEach(key => {
                const numKey = parseInt(key);
                if (numKey < index) {
                    newErrors[numKey] = prevErrors[numKey];
                } else if (numKey > index) {
                    newErrors[numKey - 1] = prevErrors[numKey];
                }
            });
            return newErrors;
        });

        setIsCalculated(false);
    };

    const handleCalculateFeesImpact = async (flagSkip: boolean = false) => {
        setFlagLoader(true)
        const toNum = (val: string) => parseFloat((val || "0").replace(/[$,%]/g, '')) || 0;

        const john = {
            Rollover: toNum(plan401kDetails.planAssumptions.rollover),
            Annual_contribution: toNum(plan401kDetails.planAssumptions.annualContribution),
            Annual_growth: toNum(plan401kDetails.planAssumptions.annualPlanGrowth) / 100,
            total_direct_fees: toNum(plan401kDetails.fees.acrylic.annualFeesPaidByBusiness.totalAnnualFeesPaidByBusiness),
            total_invest_fees: toNum(plan401kDetails.fees.acrylic.annualFeesPaidByPlanAssets.totalAannualFeesPaidByPlanAssets) / 100
        };

        const comp = {
            Rollover: toNum(plan401kDetails.planAssumptions.rollover),
            Annual_contribution: toNum(plan401kDetails.planAssumptions.annualContribution),
            Annual_growth: toNum(plan401kDetails.planAssumptions.annualPlanGrowth) / 100,
            total_direct_fees: toNum(plan401kDetails.fees.otherCompany.annualFeesPaidByBusiness.totalAnnualFeesPaidByBusiness),
            total_invest_fees: toNum(plan401kDetails.fees.otherCompany.annualFeesPaidByPlanAssets.totalAannualFeesPaidByPlanAssets) / 100
        };

        const yearsToShow = [1, 5, 10, 20];
        const results: any = [];

        const totalDirectFeeDiff = comp.total_direct_fees - john.total_direct_fees;

        let saving_direct_fee = 0;
        let saving_in_percentage_fee = 0;
        let final_amt_john = 0;
        let final_amt_comp = 0;

        for (let i = 0; i < 20; i++) {
            let start_john = 0;
            let start_comp = 0;

            if (i === 0) {
                start_john = john.Rollover + john.Annual_contribution;
                start_comp = comp.Rollover + comp.Annual_contribution;
                saving_direct_fee = totalDirectFeeDiff;
            } else {
                start_john = final_amt_john + john.Annual_contribution;
                start_comp = final_amt_comp + comp.Annual_contribution;
                saving_direct_fee = totalDirectFeeDiff + saving_direct_fee * (1 + john.Annual_growth);
            }

            const return_john = start_john * john.Annual_growth;
            const fees_john = (start_john + return_john) * john.total_invest_fees;
            const total_fees_john = fees_john + john.total_direct_fees;
            final_amt_john = start_john + return_john - total_fees_john;

            const return_comp = start_comp * comp.Annual_growth;
            const fees_comp = (start_comp + return_comp) * comp.total_invest_fees;
            const total_fees_comp = fees_comp + comp.total_direct_fees;
            final_amt_comp = start_comp + return_comp - total_fees_comp;

            const fee_diff = total_fees_john - total_fees_comp;
            const count_fee_diff = fees_comp - fees_john;

            if (i === 0) {
                saving_in_percentage_fee = count_fee_diff;
            } else {
                saving_in_percentage_fee = count_fee_diff + saving_in_percentage_fee * (1 + comp.Annual_growth);
            }

            results.push({
                year: i + 1,
                totalDifference: (Math.ceil(saving_direct_fee + saving_in_percentage_fee)).toString(),
                yearEndingBalance: {
                    acrylic: final_amt_john.toFixed(2),
                    otherCompany: final_amt_comp.toFixed(2)
                },
                saving: {
                    dueToDirectFees: Math.ceil(saving_direct_fee).toString(),
                    dueToPlanAssetsFees: Math.ceil(saving_in_percentage_fee).toString()
                }
            });
        }

        setPlan401kDetails(prev => ({
            ...prev,
            feesOnBalance: {
                year: results
            }
        }));

        setIsCalculated(true);
        setFlagLoader(false)

        const hasAcrylicChanged = !isDeepEqual(plan401kDetails.fees.acrylic, lastCheckedAcrylicRef.current);
        if (isProfileAdmin) {
            if (hasAcrylicChanged && !flagSkip) {
                setFlagOpenUpdateCalculateDialog(true);
            }
        }

        return results;
    };

    const handleValidateFormCalulcation = async () => {
        await handleSaveAcrylicSaveDetails();
        lastCheckedAcrylicRef.current = JSON.parse(JSON.stringify(plan401kDetails.fees.acrylic));
        setFlagOpenUpdateCalculateDialog(false);
    }

    // onchange function:
    // const handleBusinessFeeChange = (
    //     company: "acrylic" | "otherCompany",
    //     field: keyof typeof plan401kDetails.fees.acrylic.annualFeesPaidByBusiness | "additionalOtherFees",
    //     rawValue: string,
    //     index?: any,
    //     subField?: "feeName" | "feeValue"
    // ) => {
    //     setPlan401kDetails((prev: any) => {
    //         const toNum = (v: string) => parseCurrency(v);

    //         // Handle feeName changes - sync across both companies
    //         if (field === "additionalOtherFees" && typeof index === "number" && subField === "feeName") {
    //             const trimmedValue = rawValue.trim().toLowerCase();

    //             // Check for duplicates in the current company
    //             const currentCompanyData = prev.fees[company].annualFeesPaidByBusiness;
    //             const isDuplicate = currentCompanyData.additionalOtherFees.some((fee: any, i: number) => i !== index && fee.feeName.trim().toLowerCase() === trimmedValue);

    //             // Set validation error
    //             setFeeNameErrors(prevErrors => {
    //                 const newErrors = { ...prevErrors };
    //                 if (!trimmedValue) {
    //                     newErrors[index] = "Fee name must be unique and non-empty";
    //                 } else if (isDuplicate) {
    //                     newErrors[index] = "Fee name must be unique and non-empty";
    //                 } else {
    //                     delete newErrors[index];
    //                 }
    //                 return newErrors;
    //             });


    //             // Update fee name for both companies
    //             const acrylicFees = [...prev.fees.acrylic.annualFeesPaidByBusiness.additionalOtherFees];
    //             const otherCompanyFees = [...prev.fees.otherCompany.annualFeesPaidByBusiness.additionalOtherFees];

    //             // Ensure both arrays have the same length and update the fee name
    //             if (acrylicFees[index]) {
    //                 acrylicFees[index] = { ...acrylicFees[index], feeName: rawValue };
    //             }
    //             if (otherCompanyFees[index]) {
    //                 otherCompanyFees[index] = { ...otherCompanyFees[index], feeName: rawValue };
    //             }

    //             // Calculate totals for both companies
    //             const calculateTotal = (companyData: any, additionalFees: any[]) => {
    //                 const flatSum = toNum(companyData.setUpFee) + toNum(companyData.conversionFee) + toNum(companyData.adminFee);
    //                 const perParticipant = toNum(companyData.numberOfEmployees) * toNum(companyData.perParticipationAnnualFee);
    //                 const otherFeeVal = toNum(companyData.otherFee);
    //                 const additionalSum = additionalFees.reduce((acc, fee) => acc + toNum(fee.feeValue), 0);
    //                 return flatSum + perParticipant + otherFeeVal + additionalSum;
    //             };

    //             const acrylicTotal = calculateTotal(prev.fees.acrylic.annualFeesPaidByBusiness, acrylicFees);
    //             const otherCompanyTotal = calculateTotal(prev.fees.otherCompany.annualFeesPaidByBusiness, otherCompanyFees);

    //             return {
    //                 ...prev,
    //                 fees: {
    //                     ...prev.fees,
    //                     acrylic: {
    //                         ...prev.fees.acrylic,
    //                         annualFeesPaidByBusiness: {
    //                             ...prev.fees.acrylic.annualFeesPaidByBusiness,
    //                             additionalOtherFees: acrylicFees,
    //                             totalAnnualFeesPaidByBusiness: formatDollar(acrylicTotal.toFixed(2))
    //                         }
    //                     },
    //                     otherCompany: {
    //                         ...prev.fees.otherCompany,
    //                         annualFeesPaidByBusiness: {
    //                             ...prev.fees.otherCompany.annualFeesPaidByBusiness,
    //                             additionalOtherFees: otherCompanyFees,
    //                             totalAnnualFeesPaidByBusiness: formatDollar(otherCompanyTotal.toFixed(2))
    //                         }
    //                     }
    //                 }
    //             };
    //         }

    //         // Handle all other changes (regular fields and feeValue)
    //         const companyData = prev.fees[company].annualFeesPaidByBusiness;
    //         let updatedAdditionalFees = [...companyData.additionalOtherFees];

    //         if (field === "additionalOtherFees" && typeof index === "number" && subField === "feeValue") {
    //             const stripped = rawValue.replace(/[$,]/g, '');
    //             if (!/^\d*\.?\d*$/.test(stripped)) {
    //                 return prev;
    //             }
    //             updatedAdditionalFees[index] = {
    //                 ...updatedAdditionalFees[index],
    //                 feeValue: formatDollar(stripped)
    //             };
    //         }

    //         // Update the company data
    //         const updatedCompanyData = {
    //             ...companyData,
    //             // Update the specific field if it's not additionalOtherFees
    //             ...(field !== "additionalOtherFees" ? {
    //                 [field]: field === "numberOfEmployees" ? rawValue.replace(/\D/g, '') : formatDollar(rawValue.replace(/[$,]/g, ''))
    //             } : {}),
    //             additionalOtherFees: updatedAdditionalFees
    //         };

    //         // Calculate the total
    //         const flatSum = toNum(updatedCompanyData.setUpFee) + toNum(updatedCompanyData.conversionFee) + toNum(updatedCompanyData.adminFee);
    //         const perParticipant = toNum(updatedCompanyData.numberOfEmployees) * toNum(updatedCompanyData.perParticipationAnnualFee);
    //         const otherFeeVal = toNum(updatedCompanyData.otherFee);
    //         const additionalSum = updatedAdditionalFees.reduce((acc, fee) => acc + toNum(fee.feeValue), 0);
    //         const newTotal = flatSum + perParticipant + otherFeeVal + additionalSum;

    //         // Return the updated state
    //         return {
    //             ...prev,
    //             fees: {
    //                 ...prev.fees,
    //                 [company]: {
    //                     ...prev.fees[company],
    //                     annualFeesPaidByBusiness: { ...updatedCompanyData, totalAnnualFeesPaidByBusiness: formatDollar(newTotal.toFixed(2)) }
    //                 }
    //             }
    //         };
    //     });

    //     setIsCalculated(false);
    // };

    return (
        <>
            {PDFLoader && <GlobalPDFLoader />}
            {!isBelow1200 &&
                <AppNavbar />
            }
            <DashboardNavbar
                onClick={handleClick}
                headName="401 (K) Plan Fee Comparison"
                children={
                    <Plan401kChildren
                        handleCalculateFeesImpact={handleCalculateFeesImpact}
                        handleFeeComparisonPdf={handleFeeComparisonPdf}
                        plan401kDetails={plan401kDetails}
                        resultRef={resultRef}
                        displayedYearData={displayedYearData}
                    />
                }
            />
            {flagOpenUpdateCalculateDialog &&
                <GlobalDialog
                    flagHeadSection={true}
                    open={flagOpenUpdateCalculateDialog}
                    maxWidth="sm"
                    dialogHead={'Brokers Alliance MAC'}
                    onClose={() => {
                        // lastCheckedAcrylicRef.current = JSON.parse(JSON.stringify(plan401kDetails.fees.acrylic));
                        setFlagOpenUpdateCalculateDialog(false);
                    }}
                    view={
                        <GlobalWarningDialog
                            popupDescription={`It looks like you've modified the Acrylic calculation inputs. Do you want to update the Acrylic details with these changes?`}
                            primaryButtonText={"Update"}
                            secondaryButtonText={"Cancel"}
                            primaryButtonWidth="175px"
                            secondaryButtonWidth="175px"
                            onDelete={() => { handleValidateFormCalulcation() }}
                            handleClose={() => {
                                // lastCheckedAcrylicRef.current = JSON.parse(JSON.stringify(plan401kDetails.fees.acrylic));
                                setFlagOpenUpdateCalculateDialog(false);

                            }}
                        />
                    }
                />
            }

            <Grid container>
                <Grid size={{ xs: 12 }} className={`${styles.plan401kFormItem}`}>
                    <Grid container>
                        <Grid size={{ xs: 12 }}>
                            <TableContainer>
                                <Table size='small' className={`${globalStyles.gblTableStyles}`}>
                                    <TableHead className={`${globalStyles.gblTableHeadStyles}`}>
                                        <TableRow >
                                            <TableCell colSpan={3} className={`${globalStyles.gblTableHeadCell}`} sx={{ borderRadius: "5px 5px 0px 0px" }}>
                                                <Typography
                                                    component={'span'}
                                                    className={`${globalStyles.globalTextV14}`}
                                                >
                                                    PLAN ASSUMPTIONS
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow className={`${globalStyles.gblTableBodyRow}`}>
                                            <TableCell className={`${globalStyles.gblTableCellText}  ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : ""}`}>
                                                Company Name
                                            </TableCell>
                                            <TableCell
                                                className={`${globalStyles.gblTableCellText}  ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                align='right'
                                            >
                                                <GlobalTextfield
                                                    autoComplete='off'
                                                    fullWidth={true}
                                                    variant='outlined'
                                                    size='small'
                                                    value={plan401kDetails.planAssumptions.companyName}
                                                    onChange={(e) => handleAssumptionsChange("companyName", e.target.value)}
                                                    stylingClassName={`${globalStyles.gblTableField} ${navbarStyles.gblPlan401kFields}`}

                                                />
                                            </TableCell>
                                        </TableRow>

                                        <TableRow component={'th'} scope='row' className={`${globalStyles.gblTableBodyRow}`}>
                                            <TableCell className={`${globalStyles.gblTableCellText}  ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : ""}`}>
                                                Transferred Assets (Rollover)
                                            </TableCell>
                                            <TableCell
                                                className={`${globalStyles.gblTableCellText}  ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                align='right'
                                                sx={{ maxWidth: "250px", width: "250px" }}
                                            >
                                                <GlobalTextfield
                                                    autoComplete='off'
                                                    fullWidth={true}
                                                    variant='outlined'
                                                    size='small'
                                                    value={plan401kDetails.planAssumptions.rollover}
                                                    onChange={(e) => handleAssumptionsChange("rollover", e.target.value)}
                                                    stylingClassName={`${globalStyles.gblTableField} ${navbarStyles.gblPlan401kFields}`}
                                                    inputProps={{
                                                        maxLength: 11,
                                                        inputMode: 'numeric',
                                                        pattern: '[0-9]*',
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>

                                        <TableRow className={`${globalStyles.gblTableBodyRow}`}>
                                            <TableCell className={`${globalStyles.gblTableCellText}  ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : ""}`}>
                                                Annual Contribution
                                            </TableCell>
                                            <TableCell
                                                className={`${globalStyles.gblTableCellText}  ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                align='right'
                                            >
                                                <GlobalTextfield
                                                    autoComplete='off'
                                                    fullWidth={true}
                                                    variant='outlined'
                                                    size='small'
                                                    value={plan401kDetails.planAssumptions.annualContribution}
                                                    onChange={(e) => handleAssumptionsChange("annualContribution", e.target.value)}
                                                    stylingClassName={`${globalStyles.gblTableField} ${navbarStyles.gblPlan401kFields}`}
                                                    inputProps={{
                                                        maxLength: 11,
                                                        inputMode: 'numeric',
                                                        pattern: '[0-9]*',
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>

                                        <TableRow className={`${globalStyles.gblTableBodyRow}`}>
                                            <TableCell className={`${globalStyles.gblTableCellText}  ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : ""}`}>
                                                Annual Plan Growth (%)
                                            </TableCell>
                                            <TableCell
                                                className={`${globalStyles.gblTableCellText}  ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                align='right'
                                            >
                                                <GlobalTextfield
                                                    autoComplete='off'
                                                    fullWidth={true}
                                                    variant='outlined'
                                                    size='small'
                                                    value={plan401kDetails.planAssumptions.annualPlanGrowth}
                                                    onChange={(e) => handleAssumptionsChange("annualPlanGrowth", e.target.value)}
                                                    onBlur={(e) => handleAssumptionsChange("annualPlanGrowth", e.target.value, true)}
                                                    stylingClassName={`${globalStyles.gblTableField} ${navbarStyles.gblPlan401kFields}`}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Grid container>
                                <Grid size={{ xs: 12 }} sx={{ margin: "20px 0px 15px" }}>
                                    <Grid container sx={{ alignItems: 'center' }}>
                                        <Grid size={{ xs: 6 }}>
                                            <Typography component={'span'}
                                                className={`${globalStyles.globalTextV18} ${globalStyles.gblCWhite}`}
                                            >
                                                Annual fees paid directly by the Business
                                            </Typography>
                                        </Grid>
                                        {plan401kDetails.fees.acrylic.annualFeesPaidByBusiness.additionalOtherFees.length < 2 &&
                                            <Grid size={{ xs: 6 }} sx={{ textAlign: 'end' }}>
                                                <GlobalButton
                                                    value='Add Other Fee'
                                                    variant='outlined'
                                                    className={navbarStyles.addNewFeeBtnStyles}
                                                    size='medium'
                                                    startIcon={<AddIcon />}
                                                    onClick={() => { handleAddAdditionalOtherFee() }}
                                                />
                                            </Grid>
                                        }
                                    </Grid>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <TableContainer>
                                        <Table size='small' className={`${globalStyles.gblTableStyles}`}>
                                            <TableHead className={`${globalStyles.gblTableHeadStyles}`}>
                                                <TableRow >
                                                    <TableCell className={`${globalStyles.gblTableHeadCell}`} sx={{ borderTopLeftRadius: "5px" }}>
                                                        <Typography
                                                            component={'span'}
                                                            className={`${globalStyles.globalTextV14}`}
                                                        >
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell className={`${globalStyles.gblTableHeadCell} `}>
                                                        <Typography
                                                            component={'span'}
                                                            className={`${globalStyles.globalTextV14}`}
                                                        >
                                                            ACRYLIC
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell className={`${globalStyles.gblTableHeadCell}`} align='left' sx={{ borderTopRightRadius: "5px" }}>
                                                        <Typography
                                                            component={'span'}
                                                            className={`${globalStyles.globalTextV14}`}
                                                        >
                                                            {plan401kDetails?.planAssumptions?.companyName || "OTHER COMPANY"}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow className={`${globalStyles.gblTableBodyRow}`}>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText}  ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : ""}`}
                                                    >
                                                        Number of Employees
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                        align='left'
                                                    >
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            value={plan401kDetails.fees.acrylic.annualFeesPaidByBusiness.numberOfEmployees}
                                                            onChange={(e) => handleBusinessFeeChange("acrylic", "numberOfEmployees", e.target.value)}
                                                            stylingClassName={`${tableFocusableFields?.numberOfEmployees ? styles.planFieldsFocusable : styles.planFieldsNonFocusable} ${navbarStyles.gblPlan401kFields}`}
                                                            onFocus={() => {
                                                                setTableFocusableFields((prev) => ({ ...prev, numberOfEmployees: true }));
                                                            }}
                                                            onBlur={() => {
                                                                setTableFocusableFields((prev) => ({ ...prev, numberOfEmployees: false }));
                                                            }}
                                                            inputProps={{
                                                                maxLength: 3,
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                'data-field-id': 'acrylic-numberOfEmployees'
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, 'businessFees', 'acrylic', 'numberOfEmployees')}
                                                        />
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                        align='left'
                                                    >
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            stylingClassName={`${globalStyles.gblTableField} ${navbarStyles.gblPlan401kFields}`}
                                                            value={plan401kDetails.fees.otherCompany.annualFeesPaidByBusiness.numberOfEmployees}
                                                            onChange={(e) => handleBusinessFeeChange("otherCompany", "numberOfEmployees", e.target.value)}
                                                            inputProps={{
                                                                maxLength: 3,
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                'data-field-id': 'otherCompany-numberOfEmployees'
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, 'businessFees', 'otherCompany', 'numberOfEmployees')}
                                                        />
                                                    </TableCell>
                                                </TableRow>

                                                <TableRow className={`${globalStyles.gblTableBodyRow}`}>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText}  ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : ""}`}
                                                    >
                                                        Set Up Fee
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                        align='left'
                                                    >
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            value={plan401kDetails.fees.acrylic.annualFeesPaidByBusiness.setUpFee}
                                                            onChange={(e) => handleBusinessFeeChange("acrylic", "setUpFee", e.target.value)}
                                                            stylingClassName={`${tableFocusableFields?.setUpFee ? styles.planFieldsFocusable : styles.planFieldsNonFocusable} ${navbarStyles.gblPlan401kFields}`}
                                                            onFocus={() => {
                                                                setTableFocusableFields((prev) => ({ ...prev, setUpFee: true }));
                                                            }}
                                                            onBlur={() => {
                                                                setTableFocusableFields((prev) => ({ ...prev, setUpFee: false }));
                                                            }}
                                                            inputProps={{
                                                                maxLength: 11,
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                'data-field-id': 'acrylic-setUpFee'
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, 'businessFees', 'acrylic', 'setUpFee')}
                                                            onInput={(e: any) => {
                                                                e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                        align='left'
                                                    >
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            stylingClassName={`${globalStyles.gblTableField} ${navbarStyles.gblPlan401kFields}`}
                                                            value={plan401kDetails.fees.otherCompany.annualFeesPaidByBusiness.setUpFee}
                                                            onChange={(e) => handleBusinessFeeChange("otherCompany", "setUpFee", e.target.value)}
                                                            inputProps={{
                                                                maxLength: 11,
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                'data-field-id': 'otherCompany-setUpFee'
                                                            }}
                                                            onInput={(e: any) => {
                                                                e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, 'businessFees', 'otherCompany', 'setUpFee')}
                                                        />
                                                    </TableCell>
                                                </TableRow>

                                                <TableRow className={`${globalStyles.gblTableBodyRow}`}>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText}  ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : ""}`}
                                                    >
                                                        Conversion Fee
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                        align='left'
                                                    >
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            value={plan401kDetails.fees.acrylic.annualFeesPaidByBusiness.conversionFee}
                                                            onChange={(e) => handleBusinessFeeChange("acrylic", "conversionFee", e.target.value)}
                                                            stylingClassName={`${tableFocusableFields?.conversionFee ? styles.planFieldsFocusable : styles.planFieldsNonFocusable} ${navbarStyles.gblPlan401kFields}`}
                                                            onFocus={() => {
                                                                setTableFocusableFields((prev) => ({ ...prev, conversionFee: true }));
                                                            }}
                                                            onBlur={() => {
                                                                setTableFocusableFields((prev) => ({ ...prev, conversionFee: false }));
                                                            }}
                                                            inputProps={{
                                                                maxLength: 11,
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                'data-field-id': 'acrylic-conversionFee'
                                                            }}
                                                            onInput={(e: any) => {
                                                                e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, 'businessFees', 'acrylic', 'conversionFee')}
                                                        />
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                        align='left'
                                                    >
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            stylingClassName={`${globalStyles.gblTableField} ${navbarStyles.gblPlan401kFields}`}
                                                            value={plan401kDetails.fees.otherCompany.annualFeesPaidByBusiness.conversionFee}
                                                            onChange={(e) => handleBusinessFeeChange("otherCompany", "conversionFee", e.target.value)}
                                                            inputProps={{
                                                                maxLength: 11,
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                'data-field-id': 'otherCompany-conversionFee'
                                                            }}
                                                            onInput={(e: any) => {
                                                                e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, 'businessFees', 'otherCompany', 'conversionFee')}
                                                        />
                                                    </TableCell>
                                                </TableRow>

                                                <TableRow className={`${globalStyles.gblTableBodyRow}`}>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText}  ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : ""}`}
                                                    >
                                                        Annual Recordkepping/Admin Fee
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                        align='left'
                                                    >
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            value={plan401kDetails.fees.acrylic.annualFeesPaidByBusiness.adminFee}
                                                            onChange={(e) => handleBusinessFeeChange("acrylic", "adminFee", e.target.value)}
                                                            stylingClassName={`${tableFocusableFields?.adminFee ? styles.planFieldsFocusable : styles.planFieldsNonFocusable} ${navbarStyles.gblPlan401kFields}`}
                                                            onFocus={() => {
                                                                setTableFocusableFields((prev) => ({ ...prev, adminFee: true }));
                                                            }}
                                                            onBlur={() => {
                                                                setTableFocusableFields((prev) => ({ ...prev, adminFee: false }));
                                                            }}
                                                            inputProps={{
                                                                maxLength: 11,
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                'data-field-id': 'acrylic-adminFee'
                                                            }}
                                                            onInput={(e: any) => {
                                                                e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, 'businessFees', 'acrylic', 'adminFee')}
                                                        />
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                        align='left'
                                                    >
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            stylingClassName={`${globalStyles.gblTableField} ${navbarStyles.gblPlan401kFields}`}
                                                            value={plan401kDetails.fees.otherCompany.annualFeesPaidByBusiness.adminFee}
                                                            onChange={(e) => handleBusinessFeeChange("otherCompany", "adminFee", e.target.value)}
                                                            inputProps={{
                                                                maxLength: 11,
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                'data-field-id': 'otherCompany-adminFee'
                                                            }}
                                                            onInput={(e: any) => {
                                                                e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, 'businessFees', 'otherCompany', 'adminFee')}
                                                        />
                                                    </TableCell>
                                                </TableRow>

                                                <TableRow className={`${globalStyles.gblTableBodyRow}`}>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText}  ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : ""}`}
                                                    >
                                                        Per Participant Annual Fee
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                        align='left'
                                                    >
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            value={plan401kDetails.fees.acrylic.annualFeesPaidByBusiness.perParticipationAnnualFee}
                                                            onChange={(e) => handleBusinessFeeChange("acrylic", "perParticipationAnnualFee", e.target.value)}
                                                            stylingClassName={`${tableFocusableFields?.perParticipationAnnualFee ? styles.planFieldsFocusable : styles.planFieldsNonFocusable} ${navbarStyles.gblPlan401kFields}`}
                                                            onFocus={() => {
                                                                setTableFocusableFields((prev) => ({ ...prev, perParticipationAnnualFee: true }));
                                                            }}
                                                            onBlur={() => {
                                                                setTableFocusableFields((prev) => ({ ...prev, perParticipationAnnualFee: false }));
                                                            }}
                                                            inputProps={{
                                                                maxLength: 11,
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                'data-field-id': 'acrylic-perParticipationAnnualFee'
                                                            }}
                                                            onInput={(e: any) => {
                                                                e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, 'businessFees', 'acrylic', 'perParticipationAnnualFee')}
                                                        />
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                        align='left'
                                                    >
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            value={plan401kDetails.fees.otherCompany.annualFeesPaidByBusiness.perParticipationAnnualFee}
                                                            onChange={(e) => handleBusinessFeeChange("otherCompany", "perParticipationAnnualFee", e.target.value)}
                                                            stylingClassName={`${globalStyles.gblTableField} ${navbarStyles.gblPlan401kFields}`}
                                                            inputProps={{
                                                                maxLength: 11,
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                'data-field-id': 'otherCompany-perParticipationAnnualFee'
                                                            }}
                                                            onInput={(e: any) => {
                                                                e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, 'businessFees', 'otherCompany', 'perParticipationAnnualFee')}
                                                        />
                                                    </TableCell>
                                                </TableRow>

                                                <TableRow className={`${globalStyles.gblTableBodyRow}`}>
                                                    <TableCell className={`${globalStyles.gblTableCellText}`}>
                                                        Other Fee
                                                    </TableCell>
                                                    <TableCell className={`${globalStyles.gblTableCellText} ${navbarStyles.planTableFieldMediumWidthStyles}`} align='left'>
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            value={plan401kDetails.fees.acrylic.annualFeesPaidByBusiness.otherFee}
                                                            onChange={(e) => handleBusinessFeeChange("acrylic", "otherFee", e.target.value)}
                                                            stylingClassName={`${tableFocusableFields?.otherFee ? styles.planFieldsFocusable : styles.planFieldsNonFocusable} ${navbarStyles.gblPlan401kFields}`}
                                                            onFocus={() => {
                                                                setTableFocusableFields((prev) => ({ ...prev, otherFee: true }));
                                                            }}
                                                            onBlur={() => {
                                                                setTableFocusableFields((prev) => ({ ...prev, otherFee: false }));
                                                            }}
                                                            inputProps={{
                                                                maxLength: 11,
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                'data-field-id': 'acrylic-otherFee'
                                                            }}
                                                            onInput={(e: any) => {
                                                                e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, 'businessFees', 'acrylic', 'otherFee')}
                                                        />
                                                    </TableCell>
                                                    <TableCell className={`${globalStyles.gblTableCellText} ${navbarStyles.planTableFieldMediumWidthStyles}`} align='left'>
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            stylingClassName={`${globalStyles.gblTableField} ${navbarStyles.gblPlan401kFields}`}
                                                            value={plan401kDetails.fees.otherCompany.annualFeesPaidByBusiness.otherFee}
                                                            onChange={(e) => handleBusinessFeeChange("otherCompany", "otherFee", e.target.value)}
                                                            inputProps={{
                                                                maxLength: 11,
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                'data-field-id': 'otherCompany-otherFee'
                                                            }}
                                                            onInput={(e: any) => {
                                                                e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, 'businessFees', 'otherCompany', 'otherFee')}
                                                        />
                                                    </TableCell>
                                                </TableRow>

                                                {Array.from({ length: maxLength }).map((_, index) => {
                                                    const acrylicFee = acrylicFees[index] || { feeName: "", feeValue: "" };
                                                    const otherCompanyFee = otherCompanyFees[index] || { feeName: "", feeValue: "" };
                                                    const isEditable = editableFeeIndexes.includes(index);
                                                    const hasError = !!feeNameErrors[index];
                                                    return (
                                                        <TableRow className={`${globalStyles.gblTableBodyRow}`} key={index}>
                                                            <TableCell className={globalStyles.gblTableCellText}>
                                                                <Grid container alignItems="center" justifyContent="space-between">
                                                                    <Grid size={{ xs: 8 }} className={styles.otherFeeItem}>
                                                                        {isEditable ? (
                                                                            <>
                                                                                <GlobalTextfield
                                                                                    autoComplete='off'
                                                                                    fullWidth
                                                                                    variant='outlined'
                                                                                    size='small'
                                                                                    value={acrylicFee.feeName}
                                                                                    onChange={(e) =>
                                                                                        handleBusinessFeeChange("acrylic", "additionalOtherFees", e.target.value, index, "feeName")
                                                                                    }
                                                                                    stylingClassName={`${globalStyles.gblTableField} ${navbarStyles.gblPlan401kFields}`}
                                                                                />
                                                                                {hasError && (
                                                                                    <Typography
                                                                                        component="span"
                                                                                        sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}
                                                                                    >
                                                                                        {feeNameErrors[index]}
                                                                                    </Typography>
                                                                                )}
                                                                            </>
                                                                        ) : (
                                                                            <Typography component={'span'} className={`${globalStyles.gblCWhite} ${globalStyles.globalTextV15}`}>
                                                                                {getDisplayFeeName(acrylicFee.feeName, index)}
                                                                            </Typography>
                                                                        )}
                                                                    </Grid>
                                                                    <Grid>
                                                                        <Grid container sx={{ gap: '5px', alignItems: 'center' }}>
                                                                            {isEditable ? (
                                                                                <IconButton
                                                                                    className={styles.editIconBtn}
                                                                                    onClick={() => setEditableFeeIndexes(prev => prev.filter(i => i !== index))}
                                                                                    disabled={isSaveDisabled(index)}
                                                                                >
                                                                                    <SaveIcon sx={{ color: "white", fontSize: 14 }} />
                                                                                </IconButton>
                                                                            ) : (
                                                                                <IconButton className={styles.editIconBtn} sx={{ padding: '2px' }} onClick={() => setEditableFeeIndexes(prev => [...prev, index])}>
                                                                                    <EditIcon sx={{ color: "white", fontSize: 14 }} />
                                                                                </IconButton>
                                                                            )}
                                                                            {index >= 0 && (
                                                                                <IconButton sx={{ padding: '2px' }} onClick={() => handleRemoveAdditionalOtherFee(index)}>
                                                                                    <Tooltip
                                                                                        title="Delete"
                                                                                        arrow
                                                                                        placement="bottom"
                                                                                        componentsProps={{
                                                                                            tooltip: { className: globalStyles.globalTooltipStyle },
                                                                                            arrow: { className: globalStyles.globalTooltipArrowStyle },
                                                                                        }}
                                                                                    >
                                                                                        <img src={deleteIcon} alt="delete" style={{ width: 26, height: 26 }} />
                                                                                    </Tooltip>
                                                                                </IconButton>
                                                                            )}
                                                                        </Grid>
                                                                    </Grid>
                                                                </Grid>
                                                            </TableCell>

                                                            <TableCell className={globalStyles.gblTableCellText}>
                                                                <GlobalTextfield
                                                                    autoComplete='off'
                                                                    fullWidth
                                                                    variant='outlined'
                                                                    size='small'
                                                                    value={acrylicFee.feeValue}
                                                                    onChange={(e) =>
                                                                        handleBusinessFeeChange("acrylic", "additionalOtherFees", e.target.value, index, "feeValue")
                                                                    }
                                                                    stylingClassName={`${globalStyles.gblTableField} ${navbarStyles.gblPlan401kFields}`}
                                                                    inputProps={{
                                                                        maxLength: 11,
                                                                        inputMode: 'numeric',
                                                                        pattern: '[0-9]*',
                                                                        'data-field-id': `acrylic-additionalOtherFees-${index}`
                                                                    }}
                                                                    onInput={(e: any) => {
                                                                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                                                    }}
                                                                    onKeyDown={(e) =>
                                                                        handleCustomNavigation(e, 'businessFees', 'acrylic', `additionalOtherFees-${index}`)
                                                                    }
                                                                />
                                                            </TableCell>

                                                            <TableCell className={globalStyles.gblTableCellText}>
                                                                <GlobalTextfield
                                                                    autoComplete='off'
                                                                    fullWidth
                                                                    variant='outlined'
                                                                    size='small'
                                                                    value={otherCompanyFee.feeValue}
                                                                    onChange={(e) =>
                                                                        handleBusinessFeeChange("otherCompany", "additionalOtherFees", e.target.value, index, "feeValue")
                                                                    }
                                                                    stylingClassName={`${globalStyles.gblTableField} ${navbarStyles.gblPlan401kFields}`}
                                                                    inputProps={{
                                                                        maxLength: 11,
                                                                        inputMode: 'numeric',
                                                                        pattern: '[0-9]*',
                                                                        'data-field-id': `otherCompany-additionalOtherFees-${index}`
                                                                    }}
                                                                    onInput={(e: any) => {
                                                                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                                                    }}
                                                                    onKeyDown={(e) =>
                                                                        handleCustomNavigation(e, 'businessFees', 'otherCompany', `additionalOtherFees-${index}`)
                                                                    }
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}


                                                <TableRow className={`${globalStyles.gblSubHeaderRow}`}>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText}  ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : ""}`}
                                                    >
                                                        Total Annual Direct Fees (Paid by the business)
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                        align='left'
                                                    >
                                                        {plan401kDetails?.fees?.acrylic?.annualFeesPaidByBusiness?.totalAnnualFeesPaidByBusiness || ""}
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                        align='left'
                                                    >
                                                        {plan401kDetails?.fees?.otherCompany?.annualFeesPaidByBusiness?.totalAnnualFeesPaidByBusiness || ""}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Grid container>
                                <Grid size={{ xs: 12 }} sx={{ margin: "20px 0px 5px" }}>
                                    <Typography component={'span'}
                                        className={`${globalStyles.globalTextV18} ${globalStyles.gblCWhite}`}
                                    >
                                        Annual fees paid from plan assets
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <TableContainer>
                                        <Table size='small' className={`${globalStyles.gblTableStyles}`}>
                                            <TableHead className={`${globalStyles.gblTableHeadStyles}`}>
                                                <TableRow>
                                                    <TableCell sx={{ borderTopLeftRadius: "5px" }} className={`${globalStyles.gblTableHeadCell}`}>
                                                        <Typography
                                                            component={'span'}
                                                            className={`${globalStyles.globalTextV14}`}
                                                        >
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell className={`${globalStyles.gblTableHeadCell}`}>
                                                        <Typography
                                                            component={'span'}
                                                            className={`${globalStyles.globalTextV14}`}
                                                        >
                                                            ACRYLIC
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell className={`${globalStyles.gblTableHeadCell}`} align='left' sx={{ borderTopRightRadius: "5px" }}>
                                                        <Typography
                                                            component={'span'}
                                                            className={`${globalStyles.globalTextV14}`}
                                                        >
                                                            {plan401kDetails?.planAssumptions?.companyName || "OTHER COMPANY"}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow className={`${globalStyles.gblTableBodyRow}`}>
                                                    <TableCell className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : ""}`}>
                                                        {"Acrylic and 3(38) Advisor fee"}
                                                    </TableCell>
                                                    <TableCell className={`${globalStyles.gblTableCellText}  ${isBelow600 ? globalStyles.gblTableSmallWidth : navbarStyles.planTableFieldMediumWidthStyles}`}>
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            value={plan401kDetails.fees.acrylic.annualFeesPaidByPlanAssets.acrylicAndAdvisorFee}
                                                            onChange={(e) => handlePlanAssetsChange("acrylic", "acrylicAndAdvisorFee", e.target.value)}
                                                            stylingClassName={`${tableFocusableFields?.advisorFee ? styles.planFieldsFocusable : styles.planFieldsNonFocusable} ${navbarStyles.gblPlan401kFields}`}
                                                            onFocus={() => {
                                                                setTableFocusableFields((prev) => ({ ...prev, advisorFee: true }));
                                                            }}
                                                            onBlur={(e) => {
                                                                setTableFocusableFields((prev) => ({ ...prev, advisorFee: false }));
                                                                handlePlanAssetsChange("acrylic", "acrylicAndAdvisorFee", e.target.value, true)
                                                            }}
                                                            inputProps={{
                                                                'data-field-id': 'acrylic-acrylicAndAdvisorFee'
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, "planAssets", "acrylic", "acrylicAndAdvisorFee")}
                                                        />
                                                    </TableCell>
                                                    <TableCell className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}>
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            stylingClassName={`${globalStyles.gblTableField} ${navbarStyles.gblPlan401kFields}`}
                                                            value={plan401kDetails.fees.otherCompany.annualFeesPaidByPlanAssets.acrylicAndAdvisorFee}
                                                            onChange={(e) => handlePlanAssetsChange("otherCompany", "acrylicAndAdvisorFee", e.target.value)}
                                                            onBlur={(e) => handlePlanAssetsChange("otherCompany", "acrylicAndAdvisorFee", e.target.value, true)}
                                                            inputProps={{
                                                                'data-field-id': 'otherCompany-acrylicAndAdvisorFee'
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, "planAssets", "otherCompany", "acrylicAndAdvisorFee")}
                                                        />
                                                    </TableCell>
                                                </TableRow>

                                                <TableRow className={`${globalStyles.gblTableBodyRow}`}>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : ""}`}
                                                    >
                                                        {"Other Assets Based Fee"}
                                                    </TableCell>
                                                    <TableCell className={`${globalStyles.gblTableCellText}  ${isBelow600 ? globalStyles.gblTableSmallWidth : navbarStyles.planTableFieldMediumWidthStyles}`}>
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            value={plan401kDetails.fees.acrylic.annualFeesPaidByPlanAssets.otherAssetsBasedFee}
                                                            onChange={(e) => handlePlanAssetsChange("acrylic", "otherAssetsBasedFee", e.target.value)}
                                                            stylingClassName={`${tableFocusableFields?.otherAssetsFees ? styles.planFieldsFocusable : styles.planFieldsNonFocusable} ${navbarStyles.gblPlan401kFields}`}
                                                            onFocus={() => {
                                                                setTableFocusableFields((prev) => ({ ...prev, otherAssetsFees: true }));
                                                            }}
                                                            onBlur={(e) => {
                                                                setTableFocusableFields((prev) => ({ ...prev, otherAssetsFees: false }));
                                                                handlePlanAssetsChange("acrylic", "otherAssetsBasedFee", e.target.value, true)
                                                            }}
                                                            inputProps={{
                                                                'data-field-id': 'acrylic-otherAssetsBasedFee'
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, "planAssets", "acrylic", "otherAssetsBasedFee")}
                                                        />
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                        align='left'
                                                    >
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            stylingClassName={`${globalStyles.gblTableField} ${navbarStyles.gblPlan401kFields}`}
                                                            value={plan401kDetails.fees.otherCompany.annualFeesPaidByPlanAssets.otherAssetsBasedFee}
                                                            onChange={(e) => handlePlanAssetsChange("otherCompany", "otherAssetsBasedFee", e.target.value)}
                                                            onBlur={(e) => handlePlanAssetsChange("otherCompany", "otherAssetsBasedFee", e.target.value, true)}
                                                            inputProps={{
                                                                'data-field-id': 'otherCompany-otherAssetsBasedFee'
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, "planAssets", "otherCompany", "otherAssetsBasedFee")}
                                                        />
                                                    </TableCell>
                                                </TableRow>

                                                <TableRow className={`${globalStyles.gblTableBodyRow}`}>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : ""}`}
                                                    >
                                                        {"Average Mutual Fund Expense"}
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText}  ${isBelow600 ? globalStyles.gblTableSmallWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                    >
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            value={plan401kDetails.fees.acrylic.annualFeesPaidByPlanAssets.averageMutualFundExpense}
                                                            onChange={(e) => handlePlanAssetsChange("acrylic", "averageMutualFundExpense", e.target.value)}
                                                            stylingClassName={`${tableFocusableFields?.avgFundExpense ? styles.planFieldsFocusable : styles.planFieldsNonFocusable} ${navbarStyles.gblPlan401kFields}`}
                                                            onFocus={() => {
                                                                setTableFocusableFields((prev) => ({ ...prev, avgFundExpense: true }));
                                                            }}
                                                            onBlur={(e) => {
                                                                setTableFocusableFields((prev) => ({ ...prev, avgFundExpense: false }));
                                                                handlePlanAssetsChange("acrylic", "averageMutualFundExpense", e.target.value, true)
                                                            }}
                                                            inputProps={{
                                                                'data-field-id': 'acrylic-averageMutualFundExpense'
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, "planAssets", "acrylic", "averageMutualFundExpense")}
                                                        />
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                        align='left'
                                                    >
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            stylingClassName={`${globalStyles.gblTableField} ${navbarStyles.gblPlan401kFields}`}
                                                            value={plan401kDetails.fees.otherCompany.annualFeesPaidByPlanAssets.averageMutualFundExpense}
                                                            onChange={(e) => handlePlanAssetsChange("otherCompany", "averageMutualFundExpense", e.target.value)}
                                                            onBlur={(e) => handlePlanAssetsChange("otherCompany", "averageMutualFundExpense", e.target.value, true)}
                                                            inputProps={{
                                                                'data-field-id': 'otherCompany-averageMutualFundExpense'
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, "planAssets", "otherCompany", "averageMutualFundExpense")}
                                                        />
                                                    </TableCell>
                                                </TableRow>

                                                <TableRow className={`${globalStyles.gblTableBodyRow}`}>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : ""}`}
                                                    >
                                                        {"Record Keeper Asset Fee (includes custody)"}
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText}  ${isBelow600 ? globalStyles.gblTableSmallWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                    >
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            value={plan401kDetails.fees.acrylic.annualFeesPaidByPlanAssets.recordKeeperAssetFee}
                                                            onChange={(e) => handlePlanAssetsChange("acrylic", "recordKeeperAssetFee", e.target.value)}
                                                            stylingClassName={`${tableFocusableFields?.recordAssetFee ? styles.planFieldsFocusable : styles.planFieldsNonFocusable} ${navbarStyles.gblPlan401kFields}`}
                                                            onFocus={() => {
                                                                setTableFocusableFields((prev) => ({ ...prev, recordAssetFee: true }));
                                                            }}
                                                            onBlur={(e) => {
                                                                setTableFocusableFields((prev) => ({ ...prev, recordAssetFee: false }));
                                                                handlePlanAssetsChange("acrylic", "recordKeeperAssetFee", e.target.value, true)
                                                            }}
                                                            inputProps={{
                                                                'data-field-id': 'acrylic-recordKeeperAssetFee'
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, "planAssets", "acrylic", "recordKeeperAssetFee")}
                                                        />
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                        align='left'
                                                    >
                                                        <GlobalTextfield
                                                            autoComplete='off'
                                                            fullWidth={true}
                                                            variant='outlined'
                                                            size='small'
                                                            stylingClassName={`${globalStyles.gblTableField} ${navbarStyles.gblPlan401kFields}`}
                                                            value={plan401kDetails.fees.otherCompany.annualFeesPaidByPlanAssets.recordKeeperAssetFee}
                                                            onChange={(e) => handlePlanAssetsChange("otherCompany", "recordKeeperAssetFee", e.target.value)}
                                                            onBlur={(e) => handlePlanAssetsChange("otherCompany", "recordKeeperAssetFee", e.target.value, true)}
                                                            inputProps={{
                                                                'data-field-id': 'otherCompany-recordKeeperAssetFee'
                                                            }}
                                                            onKeyDown={(e) => handleCustomNavigation(e, "planAssets", "otherCompany", "recordKeeperAssetFee")}
                                                        />
                                                    </TableCell>
                                                </TableRow>

                                                <TableRow className={`${globalStyles.gblSubHeaderRow}`}>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : ""}`}
                                                    >
                                                        {"Total Annual Investment Related Fees (%)"}
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText}  ${isBelow600 ? globalStyles.gblTableSmallWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                    >
                                                        {plan401kDetails?.fees?.acrylic?.annualFeesPaidByPlanAssets?.totalAannualFeesPaidByPlanAssets || ""}
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableExtraLargeWidth : navbarStyles.planTableFieldMediumWidthStyles}`}
                                                        align='left'
                                                    >
                                                        {plan401kDetails?.fees?.otherCompany?.annualFeesPaidByPlanAssets?.totalAannualFeesPaidByPlanAssets || ""}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                            </Grid>
                        </Grid>


                        <div ref={resultRef}></div>
                        <Grid size={{ xs: 12 }} sx={{ margin: '15px 0px' }}>
                            <Divider sx={{ borderColor: "#D1D1D1" }} />
                        </Grid>

                        {isCalculated && (
                            <>
                                <Grid size={{ xs: 12 }} >
                                    <Typography component={'span'} className={`${globalStyles.globalTextV26} ${globalStyles.gblCWhite}`}>
                                        Result
                                    </Typography>
                                </Grid>

                                <Grid size={{ xs: 12 }}>
                                    <Grid container>
                                        <Grid size={{ xs: 12 }} sx={{ margin: "20px 0px 10px" }}>
                                            <Typography component={'span'} className={`${globalStyles.globalTextV18} ${globalStyles.gblCWhite}`}>
                                                Comparison Fee Report
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            {/* <CanvasJSChart options={chartOptions} /> */}
                                            <Grid size={{ xs: 12 }} sx={{ marginBottom: '30px' }}>
                                                <FeeImpactChart
                                                    data={displayedYearData}
                                                    companyName={plan401kDetails.planAssumptions.companyName}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid size={{ xs: 12 }} sx={{ margin: "0px 0px 30px" }}>
                                    <Grid container>
                                        <Grid size={{ xs: 12 }} sx={{ margin: "20px 0px 5px" }}>
                                            <Grid container>
                                                <Grid size={{ xs: 6 }}>
                                                    <Typography component={'span'}
                                                        className={`${globalStyles.globalTextV18} ${globalStyles.gblCWhite}`}
                                                    >
                                                        Impact of fees on balance over time
                                                    </Typography>
                                                </Grid>
                                                <Grid size={{ xs: 6 }} sx={{ textAlign: 'end' }}>
                                                    <GlobalButton
                                                        value={showAllYears ? "Show Less" : "Show All Years"}
                                                        variant='outlined'
                                                        className={navbarStyles.addNewFeeBtnStyles}
                                                        size='medium'
                                                        onClick={() => setShowAllYears(prev => !prev)}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <TableContainer>
                                                <Table size='small' className={`${globalStyles.gblTableStyles}`}>
                                                    <TableHead>
                                                        <TableRow sx={{ background: "white" }}>
                                                            <TableCell className={`${globalStyles.gblTableHeadCell}`} sx={{ borderTopLeftRadius: "5px" }}>
                                                                <Typography
                                                                    component={'span'}
                                                                    className={`${globalStyles.globalTextV14}`}
                                                                >
                                                                    YEAR
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell className={`${globalStyles.gblTableHeadCell} `} colSpan={2} align='center' >
                                                                <Typography
                                                                    component={'span'}
                                                                    className={`${globalStyles.globalTextV14}`}
                                                                >
                                                                    YEAR ENDING BALANCE
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell className={`${globalStyles.gblTableHeadCell}`} align='center' colSpan={3} sx={{ borderTopRightRadius: "5px" }}>
                                                                <Typography
                                                                    component={'span'}
                                                                    className={`${globalStyles.globalTextV14}`}
                                                                >
                                                                    SAVING
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>

                                                        <TableRow className={`${globalStyles.gblSubHeaderRow}`}>
                                                            <TableCell colSpan={1} sx={{ borderBottom: 'none' }} />
                                                            <TableCell
                                                                className={`${globalStyles.gblTableHeadCell} ${globalStyles.gblCWhite} `}
                                                                sx={{ borderBottom: 'none' }}
                                                                align='center'
                                                            >
                                                                ACRYLIC
                                                            </TableCell>
                                                            <TableCell
                                                                className={`${globalStyles.gblTableCellText}`}
                                                                sx={{ borderBottom: 'none' }}
                                                                align='center'
                                                            >
                                                                {plan401kDetails?.planAssumptions?.companyName || "OTHER COMPANY"}
                                                            </TableCell>
                                                            <TableCell
                                                                className={`${globalStyles.gblTableCellText}`}
                                                                sx={{ borderBottom: 'none' }}
                                                                align='center'
                                                            >
                                                                DUE TO DIRECT FEES
                                                            </TableCell>
                                                            <TableCell
                                                                className={`${globalStyles.gblTableCellText}`}
                                                                sx={{ borderBottom: 'none' }}
                                                                align='center'
                                                            >
                                                                DUE TO PLAN ASSETS FEES
                                                            </TableCell>
                                                            <TableCell
                                                                className={`${globalStyles.gblTableCellText}`}
                                                                sx={{ borderBottom: 'none' }}
                                                                align='center'
                                                            >
                                                                TOTAL
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>

                                                    <TableBody>
                                                        {displayedYearData?.map((item, index) => (
                                                            <TableRow className={`${globalStyles.gblTableBodyRow}`}>
                                                                <TableCell
                                                                    className={`${globalStyles.gblTableCellText}`}
                                                                    align='left'
                                                                >
                                                                    {item.year}
                                                                </TableCell>
                                                                <TableCell
                                                                    className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableMediumWidth : ""}`}
                                                                    align='center'
                                                                >
                                                                    {Number(item.yearEndingBalance.acrylic || 0).toLocaleString("en-US", {
                                                                        style: "currency",
                                                                        currency: "USD",
                                                                        maximumFractionDigits: 0
                                                                    })}
                                                                </TableCell>
                                                                <TableCell
                                                                    className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableMediumWidth : ""}`}
                                                                    align='center'
                                                                >
                                                                    {Number(item.yearEndingBalance.otherCompany || 0).toLocaleString("en-US", {
                                                                        style: "currency",
                                                                        currency: "USD",
                                                                        maximumFractionDigits: 0
                                                                    })}
                                                                </TableCell>
                                                                <TableCell
                                                                    className={`${globalStyles.gblTableCellText}`}
                                                                    align='center'
                                                                    sx={{ minWidth: isBelow600 ? "250px" : "" }}
                                                                >
                                                                    {Number(item.saving.dueToDirectFees || 0).toLocaleString("en-US", {
                                                                        style: "currency",
                                                                        currency: "USD",
                                                                        maximumFractionDigits: 0
                                                                    })}
                                                                </TableCell>
                                                                <TableCell
                                                                    className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableMediumWidth : ""}`}
                                                                    align='center'
                                                                >
                                                                    {Number(item.saving.dueToPlanAssetsFees || 0).toLocaleString("en-US", {
                                                                        style: "currency",
                                                                        currency: "USD",
                                                                        maximumFractionDigits: 0
                                                                    })}
                                                                </TableCell>
                                                                <TableCell
                                                                    className={`${globalStyles.gblTableCellText} ${isBelow600 ? globalStyles.gblTableMediumWidth : ""}`}
                                                                    align='center'
                                                                >
                                                                    {Number(item.totalDifference || 0).toLocaleString("en-US", {
                                                                        style: "currency",
                                                                        currency: "USD",
                                                                        maximumFractionDigits: 0
                                                                    })}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </>
                        )}

                        <Grid size={{ xs: 12 }}>
                            <ComplianceDocument />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

export default DashboardPlan401k